import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { auth, db } from '@/firebase/config';
import { onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useToast } from '@/components/ui/use-toast';

interface LibrarianRouteProps {
  children: React.ReactNode;
}

const LibrarianRoute = ({ children }: LibrarianRouteProps) => {
  const [isLibrarian, setIsLibrarian] = useState<boolean | null>(() => {
    const stored = localStorage.getItem("isLibrarian");
    return stored === 'true' ? true : stored === 'false' ? false : null;
  });
  const [loading, setLoading] = useState(isLibrarian === null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const tokenResult = await getIdTokenResult(user);
        const schoolID = tokenResult.claims.schoolID as string | undefined;

        if (!schoolID) {
          setIsLibrarian(false);
          setLoading(false);
          return;
        }

        const teachersRef = collection(db, `schools/${schoolID}/teachers`);
        const q = query(teachersRef, where("email", "==", user.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const teacherDoc = querySnapshot.docs[0];
          const teacherData = teacherDoc.data();
          const isLib = Array.isArray(teacherData.teacherType) 
            ? teacherData.teacherType.includes("librarian") 
            : teacherData.teacherType === "librarian";
          
          localStorage.setItem("isLibrarian", String(isLib));
          setIsLibrarian(isLib);
        } else {
          setIsLibrarian(false);
          localStorage.setItem("isLibrarian", "false");
        }
      } catch (error) {
        console.error("Error verifying librarian status:", error);
        setIsLibrarian(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return null; 
  }

  if (!isLibrarian) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};

const AccessDenied = () => {
  const { toast } = useToast();

  useEffect(() => {
    toast({
      title: "Access Denied",
      description: "Stop trying to be cheeky! You are not a librarian.",
      variant: "destructive",
      duration: 3000,
    });
  }, [toast]);

  return <Navigate to="/faculty-dashboard" replace />;
};

export default LibrarianRoute;
